import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { streamChat, type Card } from "../../api/assistant";

export type ChatRole = "user" | "assistant";

export type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
  cards?: Card[];
};

let idSeq = 0;
const nextId = () => `m${Date.now()}-${idSeq++}`;

/**
 * Ephemeral chat state for the Bankom AI widget. The conversation lives only in
 * memory (no persistence): the full history is re-sent to the backend on every
 * turn. `send` appends the user message plus an empty assistant placeholder,
 * then streams tokens/cards into that placeholder.
 */
export function useAssistantChat() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight stream if the widget unmounts (e.g. on logout).
  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || pending) return;

      const userMsg: ChatMessage = { id: nextId(), role: "user", content: trimmed };
      const assistantMsg: ChatMessage = { id: nextId(), role: "assistant", content: "" };
      const assistantId = assistantMsg.id;

      // History = prior turns + the new user message (content only). Built from
      // the pre-append snapshot so the empty assistant placeholder is excluded;
      // any earlier empty turns are dropped to keep the payload valid.
      const history = [...messages, userMsg]
        .filter((m) => m.content.trim().length > 0)
        .map((m) => ({ role: m.role, content: m.content }));

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setPending(true);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";

      void streamChat(
        history,
        locale,
        {
          onToken: (tok) => {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + tok } : m)),
            );
          },
          onTool: () => {
            /* optional "checking your orders…" status — not surfaced in MVP */
          },
          onCards: (c) => {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, cards: c } : m)));
          },
          onDone: () => {
            setPending(false);
            abortRef.current = null;
          },
          onError: (code) => {
            const line =
              code === "rate_limited"
                ? t("assistant.error.rateLimited", "You're sending messages too fast. Please wait a moment.")
                : t("assistant.error.generic", "Something went wrong. Please try again.");
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                const sep = m.content ? "\n\n" : "";
                return { ...m, content: m.content + sep + line };
              }),
            );
            setPending(false);
            abortRef.current = null;
          },
        },
        controller.signal,
      );
    },
    [messages, pending, t, i18n],
  );

  // Convenience: cards of the most recent assistant turn.
  const cards: Card[] = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return messages[i].cards ?? [];
    }
    return [];
  })();

  return { open, setOpen, messages, pending, send, cards };
}
